import type { HTMLAttributes, ReactNode } from "react"
import { createContext, useContext, useEffect, useId } from "react"
import { createPortal } from "react-dom"
import { LuX } from "react-icons/lu"

import { cn } from "~/v2/utils/cn"

type DialogContextValue = {
  descriptionId?: string
  onOpenChange?: (open: boolean) => void
  titleId?: string
}

const DialogContext = createContext<DialogContextValue>({})

type DialogProps = {
  children?: ReactNode
  onOpenChange?: (open: boolean) => void
  open?: boolean
}

const Dialog = ({ children, onOpenChange, open }: DialogProps) => {
  const titleId = useId()
  const descriptionId = useId()

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onOpenChange?.(false)
      }
    }

    window.addEventListener("keydown", handleEscape)
    document.body.style.overflow = "hidden"

    return () => {
      window.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = ""
    }
  }, [onOpenChange, open])

  if (!open) {
    return null
  }

  return createPortal(
    <DialogContext.Provider value={{ descriptionId, onOpenChange, titleId }}>
      <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
          onClick={() => onOpenChange?.(false)}
        />
        {children}
      </div>
    </DialogContext.Provider>,
    document.body,
  )
}

type DialogPartProps<T extends HTMLElement> = HTMLAttributes<T> & {
  children?: ReactNode
  className?: string
}

const DialogContent = ({
  children,
  className,
  ...props
}: DialogPartProps<HTMLDivElement>) => {
  const { descriptionId, titleId } = useContext(DialogContext)

  return (
    <div
      aria-describedby={descriptionId}
      aria-labelledby={titleId}
      aria-modal="true"
      className={cn(
        "relative z-[1001] w-full max-w-md rounded-xl border border-border bg-background p-6 shadow-2xl",
        className,
      )}
      role="dialog"
      {...props}
    >
      {children}
    </div>
  )
}

const DialogHeader = ({
  children,
  className,
  ...props
}: DialogPartProps<HTMLDivElement>) => (
  <div className={cn("flex flex-col gap-2", className)} {...props}>
    {children}
  </div>
)

const DialogTitle = ({
  children,
  className,
  ...props
}: DialogPartProps<HTMLHeadingElement>) => {
  const { titleId } = useContext(DialogContext)

  return (
    <h2
      className={cn("text-lg font-semibold text-foreground", className)}
      id={titleId}
      {...props}
    >
      {children}
    </h2>
  )
}

const DialogDescription = ({
  children,
  className,
  ...props
}: DialogPartProps<HTMLParagraphElement>) => {
  const { descriptionId } = useContext(DialogContext)

  if (!children) {
    return null
  }

  return (
    <p
      className={cn("text-sm text-muted-foreground", className)}
      id={descriptionId}
      {...props}
    >
      {children}
    </p>
  )
}

const DialogFooter = ({
  children,
  className,
  ...props
}: DialogPartProps<HTMLDivElement>) => (
  <div className={cn("mt-6 flex justify-end gap-3", className)} {...props}>
    {children}
  </div>
)

const DialogClose = ({
  className,
  ...props
}: HTMLAttributes<HTMLButtonElement>) => {
  const { onOpenChange } = useContext(DialogContext)

  return (
    <button
      aria-label="Close dialog"
      className={cn(
        "absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
        className,
      )}
      onClick={() => onOpenChange?.(false)}
      type="button"
      {...props}
    >
      <LuX className="h-4 w-4" />
    </button>
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
}
